import React from 'react';
import { BlurView } from 'expo-blur';
import type { TouchableOpacityProps } from 'react-native';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, LoadingDots } from '@garnish/core';
import { GlassView } from '@garnish/glass';

import { useIsLiquidGlassEnabled } from '../../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationsMapSearchAreaButton = (props: Props) => {
  const { accessibilityLabel, children, disabled, isLoading, onPress } = props;

  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  const isDisabled = Boolean(disabled) || Boolean(isLoading);

  // ─── Styles ──────────────────────────────────────────────────────────

  const textStyles = isDisabled ? styles.textDisabled : styles.text;
  const backgroundStyles = [
    styles.background,
    isLiquidGlassEnabled ? undefined : styles.backgroundBlur,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      activeOpacity={ACTIVE_OPACITY}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: isDisabled, busy: Boolean(isLoading) }}
      aria-busy={isLoading}
      style={styles.button}
      disabled={isDisabled}
      onPress={onPress}
    >
      <View style={backgroundStyles} pointerEvents="none">
        {isLiquidGlassEnabled ? (
          <GlassView glassEffectStyle="regular" tintColor={GLASS_TINT_COLOR} style={styles.blur} />
        ) : (
          <BlurView intensity={BLUR_INTENSITY} tint="light" style={styles.blur} />
        )}
      </View>

      {isLoading ? (
        <LoadingDots color={theme.colors.TEXT_COLOR} />
      ) : (
        <BodyText sizeMatch={['14']} style={textStyles} pointerEvents="none">
          {children}
        </BodyText>
      )}
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTIVE_OPACITY = 0.8;
const BLUR_INTENSITY = 85;
const GLASS_TINT_COLOR = 'rgba(255, 255, 255, 0.3)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    height: 40,
    minWidth: 122,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['6'],
    borderRadius: theme.radius['3xLarge'],
    ...theme.elevations['4'],
  },
  background: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    overflow: 'hidden',
  },
  backgroundBlur: {
    backgroundColor: Platform.select({
      android: 'rgba(250, 250, 246, 0.95)',
      default: 'rgba(250, 250, 246, 0.7)',
    }),
  },
  blur: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    overflow: 'hidden',
  },
  text: {
    color: theme.colors.TEXT_COLOR,
  },
  textDisabled: {
    color: theme.colors.OPACITY.DARK_KALE.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  accessibilityLabel: string;
  children: string;
  disabled?: boolean;
  isLoading?: boolean;
  onPress?: TouchableOpacityProps['onPress'];
};
